import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
  }>;
}
const STORAGE_KEY = 'mp-install-prompt-dismissed-at';
const DISMISS_COOLDOWN_DAYS = 14;
function wasRecentlyDismissed() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (!v) return false;
    const dismissedAt = parseInt(v, 10);
    const days = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    return days < DISMISS_COOLDOWN_DAYS;
  } catch {
    return false;
  }
}
export function PWAInstallPrompt() {
  const { lang, isRTL } = useLanguage();
  const isAr = lang === 'ar';
  const [deferredPrompt, setDeferredPrompt] =
  useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  useEffect(() => {
    // QA helper — exposes a console method to reset the dismissal cooldown.
    // Run `__resetInstallPrompt()` in DevTools to re-show the banner.
    if (typeof window !== 'undefined') {
      (window as Window & { __resetInstallPrompt?: () => void }).__resetInstallPrompt = () => {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {
          // ignore
        }
        console.info('[PWA] Install prompt cooldown reset. Reload to re-trigger.');
      };
    }
    // Check if already installed (running as PWA)
    const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    setIsStandalone(standalone);
    if (standalone || wasRecentlyDismissed()) return;
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show after a small delay so it doesn't slam in immediately on first paint
      setTimeout(() => setVisible(true), 4000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    const installedHandler = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);
  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setVisible(false);
      setDeferredPrompt(null);
    }
  };
  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch {

      // ignore storage errors
    }setVisible(false);
  };
  if (isStandalone) return null;
  return (
    <AnimatePresence>
      {visible && deferredPrompt &&
      <motion.div
        initial={{
          y: 100,
          opacity: 0
        }}
        animate={{
          y: 0,
          opacity: 1
        }}
        exit={{
          y: 100,
          opacity: 0
        }}
        transition={{
          type: 'spring',
          damping: 22,
          stiffness: 280
        }}
        className={`fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[80] ${isAr ? 'font-arabic' : ''}`}
        dir={isRTL ? 'rtl' : 'ltr'}
        role="dialog"
        aria-label={isAr ? 'تثبيت التطبيق' : 'Install app'}>
        
          <div className="bg-white rounded-2xl shadow-float border border-sea-blue/10 p-4 flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center shrink-0">
              <Smartphone size={22} />
            </div>
            <div className="flex-grow min-w-0">
              <h3 className="font-bold text-sea-blue text-sm mb-0.5">
                {isAr ? 'ثبّت Alex Gems' : 'Install Alex Gems'}
              </h3>
              <p className="text-xs text-sea-blue/85 mb-3 leading-relaxed">
                {isAr ?
              'اضف الدليل لشاشتك الرئيسية للوصول السريع ولاستخدامه بدون إنترنت.' :
              'Add the guide to your home screen for quick access and offline use.'}
              </p>
              <div className="flex items-center gap-2">
                <button
                onClick={handleInstall}
                className="inline-flex items-center gap-1.5 bg-sea-blue hover:bg-sea-blue-light text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors">
                
                  <Download size={12} />
                  {isAr ? 'تثبيت' : 'Install'}
                </button>
                <button
                onClick={handleDismiss}
                className="text-xs text-sea-blue/80 hover:text-sea-blue font-medium px-2 py-1.5">
                
                  {isAr ? 'ليس الآن' : 'Not now'}
                </button>
              </div>
            </div>
            <button
            onClick={handleDismiss}
            aria-label={isAr ? 'إغلاق' : 'Close'}
            className="text-sea-blue/40 hover:text-sea-blue shrink-0 -mr-1 -mt-1 p-1 rounded-full hover:bg-sand">
            
              <X size={16} />
            </button>
          </div>
        </motion.div>
      }
    </AnimatePresence>);

}